// Commission corrections: PURE display helpers (no supabase import, no I/O).
// Used by the admin Commission tab, the admin rep-detail Commission sub-tab and
// the rep portal Commission page so all three show the same numbers. Corrections
// are never mixed into the customer-paid based computeRepMetrics.
import { paidRowCommission, paidRowRate, correctionsForRep, anchoredCorrections, findOrphanSettled, findSettlingClaims, round2 } from "./commissionCorrections";

const str = (v) => (v === null || v === undefined ? "" : String(v));
const sum = (list, pick) => (list || []).reduce((s, x) => {
  const n = Number(pick(x));
  return s + (Number.isFinite(n) ? n : 0);
}, 0);

// "+$10.00" / "-$20.00" (a signed money string for correction lines).
export function fmtSignedMoney(n) {
  const v = round2(n);
  const abs = Math.abs(v).toFixed(2);
  return (v < 0 ? "-$" : "+$") + abs;
}

export function correctionKindLabel(c) {
  if (!c) return "";
  if (c.kind === "void") return "Voided";
  return "Adjusted";
}

// One rep's corrections split by status. paidRows (optional, the COMPLETE paged
// payment list) makes an OPEN correction count only while its anchor paid row
// still exists (an Undo of the payout makes it inert). Pass null/undefined when
// the paged payment read failed: nothing is then treated as inert.
// scoped:true means the list is ALREADY limited to one rep (the rep portal reads
// through RLS, without any rep_name / rep_id filter): no repId is needed.
// detectOrphans:true (admin views with the COMPLETE paged paid rows; the rep
// portal with the rep's OWN loaded, non-empty paid rows) splits off the settled
// corrections whose settlement batch has NO payment rows (see findOrphanSettled):
//   - settled_at older than 10 minutes: `orphans` (a claim that never paid out;
//     admin can Re-open; the rep sees "Pending re-open");
//   - younger: `settling` (a payout still in flight; no Re-open, not an orphan).
// Neither is in `settled` / settledTotal (the rep is not told it was paid), and
// neither is in openTotal (not counted until an admin re-opens the orphan).
// `now` (ms) is for tests.
export function repCorrectionSummary({ corrections, repId, paidRows, scoped, detectOrphans, now } = {}) {
  const empty = { all: [], open: [], settled: [], waived: [], inert: [], orphans: [], settling: [], openTotal: 0, settledTotal: 0, waivedTotal: 0, orphanTotal: 0, settlingTotal: 0 };
  if (!repId && !scoped) return empty;
  const mine = (corrections || []).filter((c) => c && (scoped || str(c.rep_id) === str(repId)));
  const live = correctionsForRep(mine, { repId: scoped ? undefined : repId, paidRows: Array.isArray(paidRows) ? paidRows : undefined });
  const liveSet = new Set(live);
  const open = live.filter((c) => c.status === "open");
  const canDetect = !!detectOrphans && Array.isArray(paidRows) && (!scoped || paidRows.length > 0);
  const liveSettled = live.filter((c) => c.status === "settled");
  const orphans = canDetect ? findOrphanSettled({ corrections: liveSettled, paidRows, now }) : [];
  const settling = canDetect ? findSettlingClaims({ corrections: liveSettled, paidRows, now }) : [];
  const unpaidSet = new Set([...orphans, ...settling]);
  const settled = liveSettled.filter((c) => !unpaidSet.has(c));
  const waived = live.filter((c) => c.status === "waived");
  const inert = mine.filter((c) => !liveSet.has(c));
  return {
    all: live, open, settled, waived, inert, orphans, settling,
    openTotal: round2(sum(open, (c) => c.delta)),
    settledTotal: round2(sum(settled, (c) => c.delta)),
    waivedTotal: round2(sum(waived, (c) => c.delta)),
    orphanTotal: round2(sum(orphans, (c) => c.delta)),
    settlingTotal: round2(sum(settling, (c) => c.delta)),
  };
}

// Admin Commission tab totals over the listed reps. totalPending gains the
// signed open corrections as their own line; totalPaid gains settled ones
// (orphaned and settling claims excluded). `unlisted` groups the OPEN (anchored,
// when the complete paidRows is known) corrections AND the orphaned settled
// corrections (older than 10 minutes, same rule as the listed reps) of reps who
// are NOT in repIds, so money owed is never invisible:
// [{ repId, repName, count, total, orphanCount, orphanTotal }].
export function adminCorrectionTotals({ corrections, repIds, paidRows, now } = {}) {
  let openTotal = 0; let settledTotal = 0; let openCount = 0; let orphanCount = 0;
  const seen = new Set();
  (repIds || []).forEach((id) => {
    const key = str(id);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const s = repCorrectionSummary({ corrections, repId: key, paidRows, detectOrphans: true, now });
    openTotal += s.openTotal; settledTotal += s.settledTotal; openCount += s.open.length; orphanCount += s.orphans.length;
  });
  const unlistedRows = correctionsForRep((corrections || []).filter((c) => c && c.status === "open" && !seen.has(str(c.rep_id))), { paidRows: Array.isArray(paidRows) ? paidRows : undefined });
  const byRep = new Map();
  const group = (c) => {
    const k = str(c.rep_id);
    if (!byRep.has(k)) byRep.set(k, { repId: k, repName: c.rep_name || "", count: 0, total: 0, orphanCount: 0, orphanTotal: 0 });
    return byRep.get(k);
  };
  unlistedRows.forEach((c) => { const g = group(c); g.count += 1; g.total += Number(c.delta) || 0; });
  const unlistedSettled = correctionsForRep((corrections || []).filter((c) => c && c.status === "settled" && !seen.has(str(c.rep_id))), { paidRows: Array.isArray(paidRows) ? paidRows : undefined });
  const unlistedOrphans = Array.isArray(paidRows) ? findOrphanSettled({ corrections: unlistedSettled, paidRows, now }) : [];
  unlistedOrphans.forEach((c) => { const g = group(c); g.orphanCount += 1; g.orphanTotal += Number(c.delta) || 0; });
  const unlisted = Array.from(byRep.values()).map((g) => ({ ...g, total: round2(g.total), orphanTotal: round2(g.orphanTotal) }));
  return { openTotal: round2(openTotal), settledTotal: round2(settledTotal), openCount, orphanCount, unlistedOpenCount: unlistedRows.length, unlistedOrphanCount: unlistedOrphans.length, unlisted };
}

// Rep-facing "Net owed" = pending payout + open corrections (signed).
export function netOwed({ pendingPayout, openTotal } = {}) {
  return round2((Number(pendingPayout) || 0) + (Number(openTotal) || 0));
}

// YTD paid out gains the settled corrections whose settled_at falls in `year`.
export function settledCorrectionsForYear(corrections, year) {
  const y = Number(year);
  return round2(sum((corrections || []).filter((c) => {
    if (!c || c.status !== "settled" || !c.settled_at) return false;
    const d = new Date(c.settled_at);
    return !isNaN(d.getTime()) && d.getFullYear() === y;
  }), (c) => c.delta));
}
export function ytdPaidWithCorrections({ ytdPaid, corrections, year } = {}) {
  return round2((Number(ytdPaid) || 0) + settledCorrectionsForYear(corrections, year));
}

// Frozen lines of one paid batch. Reads the amounts and rate that were PAID
// (commission_amount / commission_rate of the paid rows), never total x current
// rate. An id that is in the paid rows but not among the batch's resolved
// (non-cancelled) invoices is returned as a voided line so the batch count and
// the rows agree. Returns null for a legacy (synthesised) batch or a batch with
// no paid rows: the caller keeps the old rendering.
//   batch: { batchId, invoices, legacy }, paidRows: the rep's paid rows,
//   allInvoices (optional): the UNFILTERED invoice list, to tell a cancelled
//   invoice from one that is merely no longer attributed to this rep.
export function frozenBatchLines({ batch, paidRows, corrections, allInvoices } = {}) {
  if (!batch || batch.legacy) return null;
  const bid = str(batch.batchId);
  const rows = (paidRows || []).filter((r) => r && r.invoice_id && (str(r.batch_id) || "__no_batch__" + str(r.invoice_id)) === bid);
  if (rows.length === 0) return null;
  const resolved = new Map();
  (batch.invoices || []).forEach((inv) => { if (inv && inv.id != null) resolved.set(str(inv.id), inv); });
  const everything = new Map();
  (allInvoices || []).forEach((inv) => { if (inv && inv.id != null) everything.set(str(inv.id), inv); });
  const seen = new Set();
  const lines = [];
  rows.forEach((r, idx) => {
    const id = str(r.invoice_id);
    // ONE line per paid row: a duplicate payout row for the same invoice gets its
    // own line (flagged duplicate, no corrections, no Undo) so the lines add up
    // to the batch header, which sums every row.
    const duplicate = seen.has(id);
    seen.add(id);
    const inv = resolved.get(id) || null;
    const other = everything.get(id) || null;
    const cancelled = !inv && !!other && str(other.status).toLowerCase() === "cancelled";
    lines.push({
      id,
      rowKey: (r.id !== undefined && r.id !== null ? "r" + r.id : id) + "#" + idx,
      duplicate,
      inv: inv || other || null,
      commission: paidRowCommission(r),
      rate: paidRowRate(r),
      invoiceAmount: Number(r.invoice_amount),
      voided: !inv,
      cancelled,
      corrections: duplicate ? [] : anchoredCorrections(corrections, id, bid),
    });
  });
  // Keep the resolved invoices' own order where possible.
  const order = new Map((batch.invoices || []).map((inv, i) => [str(inv.id), i]));
  lines.sort((a, b) => (order.has(a.id) ? order.get(a.id) : 1e9) - (order.has(b.id) ? order.get(b.id) : 1e9));
  return lines;
}

// Correction-only settlements ('corr-...' batches) as history rows built from
// the corrections themselves (they have no rep_commission_payments rows).
// Also returns, for a normal batch id, the corrections settled with it.
export function settlementBatches(corrections) {
  const byBatch = new Map();
  (corrections || []).forEach((c) => {
    if (!c || c.status !== "settled" || !c.settled_batch_id) return;
    const k = str(c.settled_batch_id);
    if (!byBatch.has(k)) byBatch.set(k, []);
    byBatch.get(k).push(c);
  });
  const out = new Map();
  byBatch.forEach((list, batchId) => {
    const first = list[0];
    out.set(batchId, {
      batchId,
      correctionsOnly: batchId.startsWith("corr-"),
      repId: first.rep_id,
      repName: first.rep_name || "",
      paidAt: first.settled_at || null,
      method: first.settled_method || "",
      notes: first.settled_notes || "",
      total: round2(sum(list, (c) => c.delta)),
      count: list.length,
      corrections: list,
    });
  });
  return out;
}
export function correctionOnlySettlements(corrections) {
  return Array.from(settlementBatches(corrections).values())
    .filter((b) => b.correctionsOnly)
    .sort((a, b) => (new Date(b.paidAt || 0).getTime()) - (new Date(a.paidAt || 0).getTime()));
}
